import React, { useState, useEffect, useReducer, useMemo } from "react";
import { Form, Alert } from "react-bootstrap";
import { FaPlus, FaTimes, FaCheck, FaArrowRight, FaInfoCircle } from "react-icons/fa";
import {
  FaCalendarAlt,
  FaClock,
  FaTrashAlt,
  FaExclamationCircle,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TaxSettingsDrawer from "../TaxSettings";
import { GetTaxSettings } from "../../../../api/Webservices";

const PricingTab = ({
  newTicket,
  setNewTicket,
  errors,
  setErrors,
  handleAddVariablePrice,
  handleVariablePriceChange,
  handleDeleteVariablePrice,
  handleDateChange,
  handleTimeChange,
  isGuestTicket = false,
}) => {
  const [showTaxSettingsDrawer, setShowTaxSettingsDrawer] = useState(false);
  console.log(newTicket, "newTicket");

  const openTaxSettingsDrawer = () => {
    setShowTaxSettingsDrawer(true);
  };

  const closeTaxSettingsDrawer = () => {
    setShowTaxSettingsDrawer(false);
  };

  const computeTaxType = (taxEnabled, taxIncluded) => {
    return taxEnabled
      ? taxIncluded
        ? "Including Tax"
        : "Excluding Tax"
      : "No Tax";
  };

  const logTaxSettings = (updatedTicket) => {
    const taxType = computeTaxType(
      updatedTicket.taxEnabled,
      updatedTicket.taxIncluded
    );
    console.log(
      `${isGuestTicket ? "Guest Ticket" : "Main Ticket"} Tax Settings Updated:`,
      {
        tax_type: taxType,
        tax_options: updatedTicket.taxOptions || [],
        tax_registration_number: updatedTicket.taxRegisTrationNumber || null,
      }
    );
  };

  const handleTaxSettingsSave = async () => {
    try {
      const eventId = localStorage.getItem("eventId");
      const response = await GetTaxSettings(eventId);
      if (
        response &&
        response.event_settings &&
        response.event_settings.tax_settings
      ) {
        const taxSettingsData = response.event_settings.tax_settings;
        const updatedTaxOptions = taxSettingsData.tax_options || [];
        const updatedTaxRegistrationNumber =
          taxSettingsData.tax_registration_number || null;
        const updatedTaxIncluded =
          taxSettingsData.tax_display === "include_in_price";
        const totalTaxRate = updatedTaxOptions.reduce(
          (sum, tax) => sum + Number(tax.tax_percentage || 0),
          0
        );
        console.log(
          "taxSettingsData.tax_display:",
          taxSettingsData.tax_display
        );
        const updatedTicket = {
          ...newTicket,
          taxEnabled: true,
          taxOptions: updatedTaxOptions,
          taxoptionsShower: updatedTaxOptions,
          taxRegisTrationNumber: updatedTaxRegistrationNumber,
          taxIncluded:
            taxSettingsData.tax_display === "add_to_total" ||
              taxSettingsData.tax_display === "add_at_checkout"
              ? false
              : updatedTaxIncluded !== undefined
                ? updatedTaxIncluded
                : false, // Default to false if undefined
          taxDisplay:
            taxSettingsData.tax_display === "add_to_total" ||
              taxSettingsData.tax_display === "add_at_checkout"
              ? "add_at_checkout"
              : updatedTaxIncluded !== undefined
                ? updatedTaxIncluded
                  ? "include_in_price"
                  : "add_at_checkout"
                : "add_at_checkout", // Default to add_at_checkout
          taxType:
            updatedTaxIncluded !== undefined
              ? updatedTaxIncluded
                ? "Including Tax"
                : "Excluding Tax"
              : "Excluding Tax",
          taxRate: totalTaxRate.toString(),
        };

        console.log("updatedTaxIncluded: ", updatedTicket);

        setNewTicket(updatedTicket);
        logTaxSettings(updatedTicket);
        forceUpdate(); // Force re-render after state update
      }
    } catch (error) {
      console.error("Error fetching tax settings after save:", error);
    }
    closeTaxSettingsDrawer();
  };

  useEffect(() => {
    // Normalize initial taxDisplay and taxIncluded if invalid
    if (
      newTicket.taxDisplay &&
      !["include_in_price", "add_at_checkout"].includes(newTicket.taxDisplay)
    ) {
      const updatedTicket = {
        ...newTicket,
        taxDisplay:
          newTicket.taxDisplay === "add_to_total"
            ? "add_at_checkout"
            : "add_at_checkout",
        taxIncluded:
          newTicket.taxDisplay === "add_to_total"
            ? false
            : newTicket.taxIncluded === "true" ||
            newTicket.taxIncluded === true,
        taxType:
          newTicket.taxDisplay === "add_to_total"
            ? "Excluding Tax"
            : computeTaxType(newTicket.taxEnabled, newTicket.taxIncluded),
      };
      setNewTicket(updatedTicket);
      logTaxSettings(updatedTicket);
    }
    console.log("Rendering with taxDisplay:", newTicket.taxDisplay);
  }, [newTicket.taxDisplay]);

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    forceUpdate();
  }, [newTicket.taxDisplay]);

  const handleTaxDisplayChange = (e) => {
    const value = e.target.value;
    const updatedTicket = {
      ...newTicket,
      taxDisplay: value,
      taxIncluded: value === "include_in_price",
      taxType: value === "include_in_price" ? "Including Tax" : "Excluding Tax",
    };
    setNewTicket(updatedTicket);
    logTaxSettings(updatedTicket);
  };

  // Memoized price breakdown calculation for fixed price
  const priceBreakdown = useMemo(() => {
    if (!newTicket.taxEnabled || newTicket.priceOption !== "fixed" || !newTicket.fixedPrice) {
      return null;
    }
    const price = parseFloat(newTicket.fixedPrice) || 0;
    if (price <= 0) return null;
    const taxOptions = newTicket.taxoptionsShower || newTicket.taxOptions || [];
    const rate = taxOptions.reduce((sum, tax) => sum + Number(tax.tax_percentage || 0), 0) / 100;
    let base, taxAmount, total;
    if (newTicket.taxDisplay === "include_in_price") {
      // Price is inclusive: the displayed price is total
      total = price;
      base = total / (1 + rate);
      taxAmount = total - base;
    } else {
      // Price is exclusive: add tax to base price
      base = price;
      taxAmount = base * rate;
      total = base + taxAmount;
    }
    return {
      base: base.toFixed(2),
      tax: taxAmount.toFixed(2),
      total: total.toFixed(2),
      rate: (rate * 100).toFixed(0),
    };
  }, [newTicket.taxEnabled, newTicket.priceOption, newTicket.fixedPrice, newTicket.taxDisplay, newTicket.taxoptionsShower, newTicket.taxOptions]);

  const currency = newTicket.currency || 'INR';
  const taxName = newTicket.taxoptionsShower?.[0]?.tax_name || 'GST';

  return (
    <div key={newTicket.taxDisplay} className="pricing-tab">
      <div className="pricing-type-buttons">
        <div className="pricing-toggle-wrapper mb-3 mt-4">
          <button
            className={`pricing-toggle-btn ${newTicket.pricingType === "free" ? "active" : ""
              }`}
            onClick={() => {
              const updatedTicket = {
                ...newTicket,
                pricingType: "free",
                priceOption: null,
              };
              setNewTicket(updatedTicket);
              logTaxSettings(updatedTicket);
            }}
          >
            Free
          </button>

          <button
            className={`pricing-toggle-btn ${newTicket.pricingType === "paid" ? "active" : ""
              }`}
            onClick={() => {
              const updatedTicket = {
                ...newTicket,
                pricingType: "paid",
                priceOption: "fixed",
                variablePrices: [],
              };
              setNewTicket(updatedTicket);
              logTaxSettings(updatedTicket);
            }}
          >
            Paid
          </button>
        </div>

        {newTicket.pricingType === "free" && (
          <div className="offer_text_nocast">
            <p>Offer tickets at no cost</p>
          </div>
        )}
      </div>

      {newTicket.pricingType === "paid" && (
        <>
          <Form.Group className="mb-4">
            <Form.Label>
              Currency <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              className={errors.currency ? "is-invalid" : ""}
              value={newTicket.currency}
              onChange={(e) => {
                setNewTicket({
                  ...newTicket,
                  currency: e.target.value,
                });
                if (e.target.value) {
                  setErrors({ ...errors, currency: "" });
                }
              }}
              required
            >
              <option value="">Select</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="INR">INR - Indian Rupee</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="SGD">SGD - Singapore Dollar</option>
              <option value="JPY">JPY - Japanese Yen</option>
            </Form.Control>
            {errors.currency && (
              <div className="invalid-feedback d-block">
                <FaExclamationCircle className="me-1" />
                {errors.currency}
              </div>
            )}
          </Form.Group>

          <div className="pricing_option_div mt-4">
            <h3>
              Pricing options <span className="text-danger">*</span>
            </h3>

            {errors.priceOption && (
              <div className="text-danger mb-3 d-flex align-items-center">
                <FaExclamationCircle className="me-1" />
                {errors.priceOption}
              </div>
            )}

            {/* Fixed Price */}
            <Form.Check
              type="radio"
              id="fixed-price"
              label={
                <div>
                  <div>Fixed Price</div>
                  <div className="text-muted small">Charge a fixed amount</div>
                </div>
              }
              name="priceOption"
              checked={newTicket.priceOption === "fixed"}
              onChange={() => {
                const updatedTicket = { ...newTicket, priceOption: "fixed" };
                setNewTicket(updatedTicket);
                setErrors({ ...errors, priceOption: "" });
                logTaxSettings(updatedTicket);
              }}
              className="price-option-radio"
            />

            {newTicket.priceOption === "fixed" && (
              <div className="form-group mb-4">
                <input
                  type="text"
                  className={`form-control price-input ${errors.fixedPrice ? "is-invalid" : ""
                    }`}
                  placeholder="Enter Price"
                  value={newTicket.fixedPrice || ""}
                  onChange={(e) => {
                    setNewTicket({ ...newTicket, fixedPrice: e.target.value });
                    if (e.target.value.trim()) {
                      setErrors({ ...errors, fixedPrice: "" });
                    }
                  }}
                />
                {errors.fixedPrice && (
                  <div className="invalid-feedback d-block">
                    <FaExclamationCircle className="me-1" />
                    {errors.fixedPrice}
                  </div>
                )}
              </div>
            )}

            {/* Variable Price */}
            <Form.Check
              type="radio"
              id="variable-price"
              label={
                <div>
                  <div>Variable Price</div>
                  <div className="text-muted small">
                    Charge different prices at different times
                  </div>
                </div>
              }
              name="priceOption"
              checked={newTicket.priceOption === "variable"}
              onChange={() => {
                const updatedTicket = { ...newTicket, priceOption: "variable",fixedPrice: 0, };
                
                setNewTicket(updatedTicket);
                setErrors({ ...errors, priceOption: "" });
                logTaxSettings(updatedTicket);
              }}
              className="price-option-radio"
            />

            {newTicket.priceOption === "variable" && (
              <div className="variable-price-container mt-3">
                {Array.isArray(newTicket.variablePrices) &&
                  newTicket.variablePrices.map((price, index) => (
                    <div key={index} className="variable-price-row mb-3">
                      <div className="form-group row">
                        <div className="col-md-3">
                          <div className="me-2 flex-grow-1">
                            <label>Price {index + 1}</label>
                            <input
                              type="text"
                              className={`form-control ${errors[`variablePrice_${index}`] ? "is-invalid" : ""
                                }`}
                              placeholder="Enter Price"
                              value={price.price || ""}
                              onChange={(e) =>
                                handleVariablePriceChange(index, "price", e.target.value)
                              }
                            />
                            {errors[`variablePrice_${index}`] && (
                              <div className="invalid-feedback d-block">
                                <FaExclamationCircle className="me-1" />
                                {errors[`variablePrice_${index}`]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-9">
                          <div className="valid-until-container">
                            <label>Valid until</label>
                            <div className="row">
                              <div className="col-md-6">
                                <div
                                  className="date-picker-container"
                                  style={{ position: "relative" }}
                                >
                                  <div className="position-relative">
                                    <DatePicker
                                      selected={price.validUntilDate}
                                      onChange={(date) => handleDateChange(index, date)}
                                      dateFormat="dd MMM y"
                                      placeholderText="DD MMM Y"
                                      className="form-control pe-5"
                                      isClearable={true}
                                    />
                                    <FaCalendarAlt
                                      className="position-absolute"
                                      style={{
                                        right: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                        color: "#6c757d",
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div
                                  className="time-picker-container"
                                  style={{ position: "relative" }}
                                >
                                  <div className="position-relative">
                                    <DatePicker
                                      selected={price.validUntilTime}
                                      onChange={(time) => handleTimeChange(index, time)}
                                      showTimeSelect
                                      showTimeSelectOnly
                                      timeIntervals={15}
                                      timeCaption="Time"
                                      dateFormat="h:mm aa"
                                      placeholderText="hh:mm a"
                                      className="form-control pe-5"
                                      isClearable={true}
                                    />
                                    <FaClock
                                      className="position-absolute"
                                      style={{
                                        right: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                        color: "#6c757d",
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12 pt-3 text-end">
                            {Array.isArray(newTicket.variablePrices) &&
                              newTicket.variablePrices.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => handleDeleteVariablePrice(index)}
                                >
                                  <FaTrashAlt />
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleAddVariablePrice}
                >
                  <FaPlus /> Add Price
                </button>
              </div>
            )}
          </div>


          <div className="tax_options_div">
            <div className="mb-4 form-group">
              <div className="">
                <div className="form-check form-switch tax_dflex_div">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="tax-switch"
                    checked={newTicket.taxEnabled}
                    onChange={() => {
                      const enabled = !newTicket.taxEnabled;
                      const updatedTicket = {
                        ...newTicket,
                        taxEnabled: enabled,
                      };
                      setNewTicket(updatedTicket);
                      logTaxSettings(updatedTicket);
                      if (enabled) {
                        handleTaxSettingsSave();
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="tax-switch">
                    Tax
                  </label>
                </div>
                <p className="text-muted d-block mb-3">
                  If enabled, taxes will be applied to this{" "}
                  {isGuestTicket ? "guest ticket" : "ticket"}
                </p>
                <Alert
                      variant="info"
                      style={{
                        background: "#e3f2fd",
                        border: "1px solid #bbdefb",
                        borderRadius: "8px",
                        padding: "12px 15px",
                        marginBottom: "20px",
                        fontSize: "14px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <FaInfoCircle />
                        <span>
                        If the Event state and the Organization state are the same, SGST + CGST will be calculated; otherwise, if the states differ, IGST will be applied.                        </span>
                      </div>
                    </Alert>
                {newTicket.taxEnabled && (
                  <div className="border rounded p-3 bg-light">
                    <div className="fw-bold mb-2">Taxes applied</div>
                    {newTicket.taxoptionsShower && newTicket.taxoptionsShower.length > 0 ? (
                      newTicket.taxoptionsShower.map((tax, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center mb-2"
                        >
                          <div>
                            {tax.tax_name} - {tax.tax_percentage}%
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted small">
                        No taxes configured. Click "Edit Tax Settings" to add
                        taxes.
                      </div>
                    )}
                    {priceBreakdown && (
                      <div className="mt-3 p-3 bg-light border rounded">
                        <div>
                          {newTicket.taxDisplay === "include_in_price" ? (
                            <div className="fw-bold">
                              Total Amount (incl. tax): {priceBreakdown.total} {currency}
                            </div>
                          ) : (
                            <>
                              <div className="mb-2">Subtotal: {priceBreakdown.base} {currency}</div>
                              <div className="mb-2">{taxName} ({priceBreakdown.rate}%): {priceBreakdown.tax} {currency}</div>
                              <hr className="my-2" />
                              <div className="fw-bold">Total Amount: {priceBreakdown.total} {currency}</div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="d-flex align-items-center mt-3">
                      <div className="disply_form_input_cuppons">
                        <Form.Group>
                          <Form.Label className="small">Tax Display</Form.Label>
                          <Form.Control
                            as="select"
                            value={newTicket.taxDisplay}
                            onChange={handleTaxDisplayChange}
                            className="form-control-sm"
                          >
                            <option value="include_in_price">
                              Tax included in the price
                            </option>
                            <option value="add_at_checkout">
                              Add tax to the total amount at checkout
                            </option>
                          </Form.Control>
                        </Form.Group>
                      </div>
                    </div>
                    {/* <div className="mt-3">
                      <div className="text-muted small">
                        Debug: taxDisplay = '{newTicket.taxDisplay}',
                        taxIncluded = '{newTicket.taxIncluded}', taxType = '
                        {newTicket.taxType}'
                      </div>
                      <button
                        className="btn btn-outline-primary"
                        onClick={openTaxSettingsDrawer}
                      >
                        Edit Tax Settings <FaArrowRight className="ms-1" />
                      </button>
                    </div> */}
                  </div>
                )}
              </div>
            </div>
          </div>
          <TaxSettingsDrawer
            show={showTaxSettingsDrawer}
            onClose={closeTaxSettingsDrawer}
            onSave={handleTaxSettingsSave}
          />
        </>
      )}
    </div>
  );
};

export default PricingTab;